/**
 * Helpers for splitting and reassembling CURRENCY / PHONE field values that
 * carry a prefix (currency code, country code) ahead of the body (amount,
 * phone number). Replaces inline regexes that were duplicated across many
 * call sites in PdfViewer, DocumentPreview, FieldPropertiesPanel, and
 * SigningView.
 */

export type PrefixedFieldType = 'CURRENCY' | 'PHONE';

export interface PrefixSplit {
  /** The prefix matched off the front of the value (e.g. `"USD"`, `"+1"`). Empty string when no prefix is present. */
  prefix: string;
  /** Everything after the prefix and any separating whitespace or dash. */
  body: string;
}

const CURRENCY_PREFIX_RE = /^([A-Z]{3})[\s-]*(.*)$/;
const PHONE_PREFIX_RE = /^(\+\d{1,4})[\s-]*(.*)$/;

/**
 * Splits a prefixed value into `{ prefix, body }`.
 *
 * When `expectedPrefix` is supplied, a value whose prefix doesn't match it is
 * returned as `{ prefix: '', body: <full value> }` so the caller can decide
 * whether to render the value untouched or reformat it.
 */
export function splitPrefixedValue(
  fieldType: PrefixedFieldType | string,
  value: string | undefined | null,
  expectedPrefix?: string,
): PrefixSplit {
  const v = (value ?? '').toString();
  if (!v) return { prefix: '', body: '' };
  if (fieldType !== 'CURRENCY' && fieldType !== 'PHONE') {
    return { prefix: '', body: v };
  }

  const re = fieldType === 'CURRENCY' ? CURRENCY_PREFIX_RE : PHONE_PREFIX_RE;
  const match = re.exec(v);
  if (!match) return { prefix: '', body: v };

  const prefix = match[1] ?? '';
  const body = match[2] ?? '';

  if (expectedPrefix && prefix !== expectedPrefix) {
    return { prefix: '', body: v };
  }

  return { prefix, body };
}

/**
 * Convenience: returns just the body portion of a prefixed value. Drop-in
 * replacement for legacy inline prefix-strip regexes.
 */
export function stripPrefix(
  fieldType: PrefixedFieldType | string,
  value: string | undefined | null,
  expectedPrefix?: string,
): string {
  return splitPrefixedValue(fieldType, value, expectedPrefix).body;
}

/**
 * Joins a prefix and body back into a stored field value. Returns an empty
 * string when both halves are empty so callers don't persist a lone prefix
 * as the field's `defaultValue`.
 */
export function joinPrefixedValue(prefix: string, body: string): string {
  const p = prefix.trim();
  const b = body.trim();
  if (!b) return '';
  if (!p) return b;
  return `${p} ${b}`;
}

/**
 * Parses a field's `dropdownOptions` string for CURRENCY / PHONE fields.
 *
 * Convention:
 *   - `"USD"`  — currency code, signer/author may change
 *   - `"!USD"` — currency code locked at this value
 *   - `"+1"`   — country code, may change
 *   - `"!+1"`  — country code locked
 */
export function parsePrefixOptions(
  dropdownOptions: string | undefined | null,
  fallback: string,
): { prefix: string; locked: boolean } {
  const opts = (dropdownOptions ?? '').toString();
  const locked = opts.startsWith('!');
  const prefix = (locked ? opts.slice(1) : opts) || fallback;
  return { prefix, locked };
}
